import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Heart,
  GitBranch,
  Target,
  Users,
  BookOpen,
  Sprout,
  Calendar,
  Calculator,
  Settings,
  PenLine,
  Search,
  Handshake,
  RotateCcw,
} from "lucide-react";
import { QUESTIONS_BY_LANG, computeResult } from "@/lib/quiz";
import { useT } from "@/lib/LanguageContext";
import { TIDYCAL } from "@/lib/images";

const ICONS = {
  Heart, GitBranch, Target, Users, BookOpen, Sprout,
  Calendar, Calculator, Settings, PenLine, Search, Handshake,
};

const BOOK_URL = TIDYCAL.clarity;

export default function Quiz() {
  const { t, lang } = useT();
  const q = t.quiz;

  const QUESTIONS = useMemo(
    () => QUESTIONS_BY_LANG[lang] || QUESTIONS_BY_LANG.es,
    [lang]
  );

  const [stage, setStage] = useState("intro");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState([]);

  // Recompute result if user changes language while on result screen
  useEffect(() => {
    if (stage === "result") {
      setResult(computeResult(answers, lang));
    }
  }, [lang, stage, answers]);

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];
  const progress = Math.round(((step + 1) / total) * 100);

  const start = () => {
    setStage("quiz");
    setStep(0);
  };

  const pick = (optionIndex) => {
    const next = { ...answers, [current.id]: optionIndex };
    setAnswers(next);
    setTimeout(() => {
      if (step < total - 1) {
        setStep(step + 1);
      } else {
        setResult(computeResult(next, lang));
        setStage("result");
      }
    }, 220);
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
    else setStage("intro");
  };

  const restart = () => {
    setStage("intro");
    setStep(0);
    setAnswers({});
    setResult([]);
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="relative py-24 md:py-36 bg-[#F7F3F7] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F] mb-6">
              {q.eyebrow}
            </p>
            <h2
              data-testid="quiz-headline"
              className="font-serif font-light text-[clamp(2.2rem,5vw,4.5rem)] leading-[0.95] tracking-tight"
            >
              {q.headlineL1}
              <br />
              <span className="italic">{q.headlineL2}</span>
              <br />
              {q.headlineL3}
            </h2>
            <p className="mt-8 text-base text-[#6B4E6F] leading-relaxed max-w-md">
              {q.body}
            </p>

            <div className="hidden lg:block mt-12 space-y-4 text-[12px] uppercase tracking-[0.18em] text-[#6B4E6F]">
              <Bullet color="#E0B25B" text={q.meta1} />
              <Bullet color="#4B2F4F" text={q.meta2} />
              <Bullet color="#E0B25B" text={q.meta3} />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <div
              data-testid="quiz-panel"
              className="relative border vk-line-strong bg-[#F7F3F7] min-h-[520px] p-8 md:p-12"
            >
              {stage === "quiz" && (
                <div className="absolute top-0 left-0 right-0 h-px bg-[#4B2F4F]/10">
                  <motion.div
                    className="h-full bg-[#E0B25B]"
                    initial={{ width: 0 }}
                    animate={{ width: `${((step + 1) / total) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              )}

              <AnimatePresence mode="wait">
                {stage === "intro" && (
                  <motion.div
                    key="intro"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col h-full min-h-[440px] justify-center text-center"
                  >
                    <div className="inline-flex items-center gap-2 justify-center mb-6">
                      <Sparkles size={14} strokeWidth={1.4} className="text-[#E0B25B]" />
                      <span className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
                        {q.introBadge}
                      </span>
                    </div>
                    <h3 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight max-w-lg mx-auto">
                      {q.introHeadL1}
                      <br />
                      <span className="italic">{q.introHeadL2}</span>
                    </h3>
                    <p className="mt-6 text-base text-[#6B4E6F] max-w-md mx-auto leading-relaxed">
                      {q.introBody}
                    </p>
                    <button
                      onClick={start}
                      data-testid="quiz-start-button"
                      className="group mt-10 inline-flex items-center justify-center gap-3 self-center bg-[#4B2F4F] text-[#F7F3F7] px-10 py-5 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
                    >
                      {q.start}
                      <ArrowRight
                        size={16}
                        strokeWidth={1.4}
                        className="group-hover:translate-x-1 transition-transform duration-300"
                      />
                    </button>
                  </motion.div>
                )}

                {stage === "quiz" && current && (
                  <motion.div
                    key={`q-${step}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.35 }}
                    className="flex flex-col h-full min-h-[440px]"
                  >
                    <div className="flex items-center justify-between mb-8 text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
                      <span data-testid="quiz-step-indicator">{current.eyebrow}</span>
                      <span>{progress}%</span>
                    </div>

                    <h3 className="font-serif font-light text-3xl md:text-4xl leading-[1.05] tracking-tight">
                      {current.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#6B4E6F] leading-relaxed">
                      {current.subtitle}
                    </p>

                    <div className="mt-8 space-y-px bg-[#4B2F4F]/10" data-testid="quiz-options">
                      {current.options.map((opt, i) => {
                        const selected = answers[current.id] === i;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => pick(i)}
                            data-testid={`quiz-option-${step}-${i}`}
                            className={`group w-full text-left px-5 py-4 flex items-center justify-between transition-colors duration-200 ${
                              selected
                                ? "bg-[#4B2F4F] text-[#F7F3F7]"
                                : "bg-[#F7F3F7] hover:bg-[#F3D9C8]"
                            }`}
                          >
                            <span className="text-base leading-snug pr-4">
                              {opt.label}
                            </span>
                            <ArrowRight
                              size={16}
                              strokeWidth={1.4}
                              className={`flex-shrink-0 transition-all duration-300 ${
                                selected
                                  ? "translate-x-0 text-[#E0B25B]"
                                  : "-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                              }`}
                            />
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-auto pt-8 flex items-center justify-between">
                      <button
                        onClick={back}
                        data-testid="quiz-back-button"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#6B4E6F] hover:text-[#4B2F4F] transition-colors"
                      >
                        <ArrowLeft size={14} strokeWidth={1.4} />
                        {q.prev}
                      </button>
                      <span className="text-[11px] uppercase tracking-[0.22em] text-[#6B4E6F]">
                        {step + 1} / {total}
                      </span>
                    </div>
                  </motion.div>
                )}

                {stage === "result" && (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col"
                    data-testid="quiz-result"
                  >
                    <p className="text-[11px] uppercase tracking-[0.24em] text-[#E0B25B] mb-4">
                      {q.resultEyebrow}
                    </p>
                    <h3 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight">
                      {q.resultHeadL1}
                      <br />
                      <span className="italic">{q.resultHeadL2}</span>{" "}
                      {q.resultHeadL3}
                    </h3>

                    <ol className="mt-8 space-y-px bg-[#4B2F4F]/10">
                      {result.map((r, i) => {
                        const Icon = ICONS[r.icon] || Sparkles;
                        return (
                          <motion.li
                            key={r.key}
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            data-testid={`result-skill-${i}`}
                            className="bg-[#F7F3F7] p-5 flex items-start gap-5"
                          >
                            <span className="font-serif text-3xl text-[#E0B25B] leading-none w-10 flex-shrink-0">
                              0{i + 1}
                            </span>
                            <Icon
                              size={22}
                              strokeWidth={1.2}
                              className="mt-1 flex-shrink-0"
                            />
                            <div className="flex-1">
                              <p className="font-serif text-xl md:text-2xl leading-tight">
                                {r.name}
                              </p>
                              <p className="mt-1 text-sm text-[#6B4E6F] leading-relaxed">
                                {r.blurb}
                              </p>
                            </div>
                          </motion.li>
                        );
                      })}
                    </ol>

                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      className="mt-10 border-t vk-line pt-8"
                      data-testid="quiz-after-result"
                    >
                      <p className="text-[11px] uppercase tracking-[0.24em] text-[#6B4E6F]">
                        {q.afterEyebrow}
                      </p>
                      <h4 className="mt-2 font-serif text-2xl md:text-3xl leading-tight">
                        {q.afterHead}
                      </h4>
                      <p className="mt-4 text-base text-[#6B4E6F] leading-relaxed">
                        {q.afterBody}
                      </p>
                      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <button
                          type="button"
                          onClick={restart}
                          data-testid="quiz-restart-button"
                          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#6B4E6F] hover:text-[#4B2F4F] transition-colors"
                        >
                          <RotateCcw size={14} strokeWidth={1.4} />
                          {q.restart}
                        </button>
                        <a
                          href={BOOK_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="quiz-submit-button"
                          className="group inline-flex items-center gap-3 bg-[#4B2F4F] text-[#F7F3F7] px-8 py-4 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
                        >
                          {q.bookSession}
                          <ArrowRight
                            size={16}
                            strokeWidth={1.4}
                            className="group-hover:translate-x-1 transition-transform duration-300"
                          />
                        </a>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ color, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {text}
    </div>
  );
}
